import { useMemo, type Ref } from "react";

import type { WatchMomentumDraft } from "@/domains/watches/hooks/useWatchAuthoringState";
import { getAuthorableWatchSignals, type WatchSignalCatalog } from "../../lib/watchSignalCatalog";
import {
  describeMomentumCondition,
  getReviewedSignalPresenter,
  isMomentumPresenter,
} from "../../lib/watchSignalReleaseRegistry";
import styles from "./WatchMomentumFields.module.css";

export interface WatchMomentumFieldsProps {
  mode: "setup" | "alert";
  catalog: WatchSignalCatalog | null;
  draft: WatchMomentumDraft;
  ticker: string;
  errorMessageId: string;
  invalidTriggerMode?: boolean;
  invalidComparison?: boolean;
  invalidDirection?: boolean;
  invalidLevel?: boolean;
  onTriggerModeChange: (triggerMode: "STATE" | "CROSSING") => void;
  onComparisonChange: (comparison: "SIGNAL_LINE" | "ZERO") => void;
  onDirectionChange: (direction: "ABOVE" | "BELOW") => void;
  onLevelChange: (value: string) => void;
  triggerModeRef?: Ref<HTMLFieldSetElement>;
  comparisonRef?: Ref<HTMLFieldSetElement>;
  directionRef?: Ref<HTMLFieldSetElement>;
  levelRef?: Ref<HTMLInputElement>;
  className?: string;
}

/** Catalog-authorized, neutral authoring for the fixed completed-daily Momentum leaves. */
export default function WatchMomentumFields({
  mode,
  catalog,
  draft,
  ticker,
  errorMessageId,
  invalidTriggerMode = false,
  invalidComparison = false,
  invalidDirection = false,
  invalidLevel = false,
  onTriggerModeChange,
  onComparisonChange,
  onDirectionChange,
  onLevelChange,
  triggerModeRef,
  comparisonRef,
  directionRef,
  levelRef,
  className,
}: WatchMomentumFieldsProps) {
  const presenters = useMemo(() => getAuthorableWatchSignals(catalog, {
    job: "MARKET",
    subjectScope: "UNDERLYING",
  }).map(getReviewedSignalPresenter).filter(isMomentumPresenter), [catalog]);
  const availableModes = draft.indicator === "MACD" ? [] : (["STATE", "CROSSING"] as const)
    .filter((triggerMode) => presenters.some((presenter) => (
      presenter.kind === (draft.indicator === "RSI" ? "MOMENTUM_RSI" : "MOMENTUM_ROC")
        && presenter.triggerMode === triggerMode
    )));
  const availableComparisons = draft.indicator === "MACD" ? (["SIGNAL_LINE", "ZERO"] as const)
    .filter((comparison) => presenters.some((presenter) => (
      presenter.kind === "MOMENTUM_MACD" && presenter.comparison === comparison
    ))) : [];

  if (mode === "setup") {
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <p className={styles.context}>
          This measurement uses completed regular-session prices. An incomplete daily bar cannot trigger an alert.
        </p>
        {draft.indicator === "MACD" ? (
          <fieldset
            ref={comparisonRef}
            className={styles.choiceGroup}
            aria-invalid={invalidComparison || undefined}
            aria-describedby={invalidComparison ? errorMessageId : undefined}
          >
            <legend>What should the MACD line cross?</legend>
            {availableComparisons.includes("SIGNAL_LINE") ? (
              <label className={draft.comparison === "SIGNAL_LINE" ? styles.choiceSelected : styles.choice}>
                <input type="radio" name="momentum-comparison" checked={draft.comparison === "SIGNAL_LINE"} onChange={() => onComparisonChange("SIGNAL_LINE")} />
                <span><strong>Its signal line</strong><small>Alert after the MACD line crosses the signal line.</small></span>
              </label>
            ) : null}
            {availableComparisons.includes("ZERO") ? (
              <label className={draft.comparison === "ZERO" ? styles.choiceSelected : styles.choice}>
                <input type="radio" name="momentum-comparison" checked={draft.comparison === "ZERO"} onChange={() => onComparisonChange("ZERO")} />
                <span><strong>Zero</strong><small>Alert after the MACD line crosses the zero line.</small></span>
              </label>
            ) : null}
          </fieldset>
        ) : (
          <fieldset
            ref={triggerModeRef}
            className={styles.choiceGroup}
            aria-invalid={invalidTriggerMode || undefined}
            aria-describedby={invalidTriggerMode ? errorMessageId : undefined}
          >
            <legend>When should we alert?</legend>
            {availableModes.includes("STATE") ? (
              <label className={draft.triggerMode === "STATE" ? styles.choiceSelected : styles.choice}>
                <input type="radio" name="momentum-trigger-mode" checked={draft.triggerMode === "STATE"} onChange={() => onTriggerModeChange("STATE")} />
                <span><strong>When it reaches that side</strong><small>The first fresh completed-day check may alert if the condition is already true.</small></span>
              </label>
            ) : null}
            {availableModes.includes("CROSSING") ? (
              <label className={draft.triggerMode === "CROSSING" ? styles.choiceSelected : styles.choice}>
                <input type="radio" name="momentum-trigger-mode" checked={draft.triggerMode === "CROSSING"} onChange={() => onTriggerModeChange("CROSSING")} />
                <span><strong>Only after it crosses</strong><small>The first fresh check establishes a baseline. Only a later completed-day crossing can alert.</small></span>
              </label>
            ) : null}
          </fieldset>
        )}
      </div>
    );
  }

  const isState = draft.indicator !== "MACD" && draft.triggerMode === "STATE";
  const level = draft.indicator === "RSI"
    ? draft.level
    : draft.indicator === "ROC" ? draft.levelPercent : "";
  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      <fieldset
        ref={directionRef}
        className={styles.compactGroup}
        aria-invalid={invalidDirection || undefined}
        aria-describedby={invalidDirection ? errorMessageId : undefined}
      >
        <legend>{isState ? "Measurement" : "Crossing direction"}</legend>
        <label className={draft.direction === "ABOVE" ? styles.pillSelected : styles.pill}>
          <input type="radio" name="momentum-direction" checked={draft.direction === "ABOVE"} onChange={() => onDirectionChange("ABOVE")} />
          {isState ? "At or above" : "Crosses above"}
        </label>
        <label className={draft.direction === "BELOW" ? styles.pillSelected : styles.pill}>
          <input type="radio" name="momentum-direction" checked={draft.direction === "BELOW"} onChange={() => onDirectionChange("BELOW")} />
          {isState ? "At or below" : "Crosses below"}
        </label>
      </fieldset>

      {draft.indicator !== "MACD" ? (
        <label className={styles.levelField}>
          <strong>{draft.indicator === "RSI" ? "RSI level" : "Rate of change"}</strong>
          <span className={styles.levelInput}>
            <input
              ref={levelRef}
              value={level}
              onChange={(event) => onLevelChange(event.target.value)}
              inputMode="decimal"
              placeholder={draft.indicator === "RSI" ? "50" : "5"}
              aria-label={draft.indicator === "RSI" ? "RSI level" : "Rate of change percentage"}
              aria-invalid={invalidLevel || undefined}
              aria-describedby={invalidLevel ? errorMessageId : undefined}
            />
            {draft.indicator === "ROC" ? <span aria-hidden="true">%</span> : null}
          </span>
          <small>{draft.indicator === "RSI"
            ? "Enter a number greater than 0 and less than 100."
            : "Enter 5 for a 5% change, or -5 for a -5% change."}</small>
        </label>
      ) : null}

      <p className={styles.rulePreview}>
        <strong>Alert rule</strong>
        {describeMomentumCondition({ ticker, ...draft })}
      </p>
    </div>
  );
}
